/**
 * EmailValidationIndicator - Real-time Email Validation Status UI
 *
 * Displays the status of backend email validation via SSE.
 * Shows different states: validating, valid (brief), invalid (hard block).
 *
 * Design Philosophy:
 * - Minimal footprint when validation succeeds
 * - Clear, actionable error for invalid emails
 * - Fail-open: shows nothing on timeout/error (don't block user)
 *
 * @module shared/ui/EmailValidationIndicator
 */

import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Loader2, CheckCircle2, XCircle, ArrowLeft } from 'lucide-react';
import type { EmailValidationStatus } from '@features/auth/services/userAuthApi';

// ─────────────────────────────────────────────────────────────────────────────
// Component Types
// ─────────────────────────────────────────────────────────────────────────────

export interface EmailValidationIndicatorProps {
  /** Current validation status */
  status: EmailValidationStatus | 'idle' | 'error';
  /** Reason for invalid status */
  reason?: string | null;
  /** Callback when user clicks "Change email" */
  onChangeEmail?: () => void;
  /** Additional CSS classes */
  className?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Animation Variants
// ─────────────────────────────────────────────────────────────────────────────

const containerVariants = {
  initial: { opacity: 0, height: 0 },
  animate: { opacity: 1, height: 'auto' },
  exit: { opacity: 0, height: 0 },
};

// ─────────────────────────────────────────────────────────────────────────────
// Component Implementation
// ─────────────────────────────────────────────────────────────────────────────

/**
 * EmailValidationIndicator - Shows email validation status
 *
 * States:
 * - `idle`: Nothing shown
 * - `validating`: Spinner + "Verifying email..."
 * - `valid`: Brief checkmark (auto-fades)
 * - `invalid`: Error block with reason + "Change email" action (HARD BLOCK)
 * - `timeout`: Nothing shown (fail-open)
 * - `error`: Nothing shown (fail-open)
 *
 * @example
 * ```tsx
 * <EmailValidationIndicator
 *   status={validationStatus}
 *   reason={invalidReason}
 *   onChangeEmail={() => setStep('email')}
 * />
 * ```
 */
export const EmailValidationIndicator: React.FC<EmailValidationIndicatorProps> = ({
  status,
  reason,
  onChangeEmail,
  className = '',
}) => {
  // Don't render anything for idle, timeout, or error states (fail-open)
  if (status === 'idle' || status === 'timeout' || status === 'error') {
    return null;
  }

  return (
    <AnimatePresence mode="wait">
      {/* Validating State */}
      {status === 'validating' && (
        <motion.div
          key="validating"
          variants={containerVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          transition={{ duration: 0.2 }}
          className={`flex items-center gap-2 py-2 ${className}`}
        >
          <Loader2 className="w-4 h-4 text-emerald-500 animate-spin" />
          <span className="text-sm text-gray-500">Verifying email...</span>
        </motion.div>
      )}

      {/* Valid State - Brief confirmation */}
      {status === 'valid' && (
        <motion.div
          key="valid"
          variants={containerVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          transition={{ duration: 0.2 }}
          className={`flex items-center gap-2 py-2 ${className}`}
        >
          <CheckCircle2 className="w-4 h-4 text-emerald-500" />
          <span className="text-sm text-emerald-600 font-medium">Email verified</span>
        </motion.div>
      )}

      {/* Invalid State - Hard Block UI */}
      {status === 'invalid' && (
        <motion.div
          key="invalid"
          variants={containerVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          transition={{ duration: 0.2 }}
          className={className}
        >
          <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
            <div className="flex items-start gap-3">
              <XCircle className="w-5 h-5 text-red-500 mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-semibold text-red-800">
                  This email cannot receive messages
                </h4>
                {reason && (
                  <p className="text-sm text-red-600 mt-1">
                    {reason}
                  </p>
                )}
                {onChangeEmail && (
                  <button
                    type="button"
                    onClick={onChangeEmail}
                    className="inline-flex items-center gap-1.5 mt-3 text-sm font-medium text-red-700 hover:text-red-800 transition-colors"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Change email
                  </button>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default EmailValidationIndicator;
